import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Users, UserPlus, Calendar, Timer, TrendingUp, Loader2, X, Plus,
  Search, Download, ArrowRight, AlertTriangle, Clock, Filter, Eye, Edit3,
  ChevronDown, ChevronRight, Briefcase, FileText, MessageSquare, CheckCircle2,
  Circle, Phone, Mail, Star
} from "lucide-react";
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip, BarChart, Bar, XAxis, YAxis } from "recharts";
import FilterMultiSelect, { SearchSelect } from "@/components/filters/FilterMultiSelect";
import RecrutementRequestForm from "@/components/rh/RecrutementRequestForm";
import ZonesTab from "./ZonesTab";
import { RecrutementPipeline, RecrutementAnalyse, EmbaucheModal } from "./RecrutementLot2";

// ═════════════════════════════════════════════════════════════════════════
//  Constants
// ═════════════════════════════════════════════════════════════════════════
const TABS = [
  { k: "overview", l: "Vue d'ensemble" },
  { k: "zones", l: "Besoins par zone" },
  { k: "besoins", l: "Besoins & Postes" },
  { k: "candidats", l: "Candidats" },
  { k: "entretiens", l: "Entretiens" },
  { k: "integrations", l: "Intégrations" },
  { k: "stats", l: "Statistiques" },
  { k: "analyse", l: "Analyse recrutement" },
];
const PRIO = {
  critique:   { l: "Critique",   cls: "bg-rose-100 text-rose-700 border-rose-200" },
  importante: { l: "Importante", cls: "bg-orange-100 text-orange-700 border-orange-200" },
  standard:   { l: "Standard",   cls: "bg-slate-100 text-slate-600 border-slate-200" },
};
const REC_STAT = {
  nouveau: "Nouveau", a_qualifier: "À qualifier", valide: "Validé", recherche: "Recherche",
  candidats: "Candidats", entretiens: "Entretiens", retenu: "Candidat retenu",
  integration: "Intégration", pourvu: "Pourvu", annule: "Annulé",
};
const REC_STAT_CLS = {
  nouveau: "bg-blue-100 text-blue-700", a_qualifier: "bg-indigo-100 text-indigo-700",
  valide: "bg-cyan-100 text-cyan-700", recherche: "bg-purple-100 text-purple-700",
  candidats: "bg-violet-100 text-violet-700", entretiens: "bg-amber-100 text-amber-700",
  retenu: "bg-emerald-100 text-emerald-700", integration: "bg-teal-100 text-teal-700",
  pourvu: "bg-emerald-200 text-emerald-800", annule: "bg-slate-200 text-slate-500",
};
const CAND_STAT = {
  nouveau: "Nouveau", a_contacter: "À contacter", contacte: "Contacté",
  entretien_prevu: "Entretien prévu", entretien_realise: "Entretien réalisé",
  retenu: "Retenu", refuse: "Refusé", vivier: "Vivier",
};
const CAND_STAT_CLS = {
  nouveau: "bg-blue-100 text-blue-700", a_contacter: "bg-amber-100 text-amber-700",
  contacte: "bg-indigo-100 text-indigo-700", entretien_prevu: "bg-purple-100 text-purple-700",
  entretien_realise: "bg-violet-100 text-violet-700", retenu: "bg-emerald-100 text-emerald-700",
  refuse: "bg-rose-100 text-rose-700", vivier: "bg-slate-100 text-slate-600",
};
const PIPE_COLORS = ["#3b82f6", "#8b5cf6", "#a855f7", "#f59e0b", "#10b981"];
const DONUT_COLORS = ["#3b82f6", "#8b5cf6", "#f59e0b", "#10b981", "#ef4444", "#94a3b8", "#06b6d4", "#eab308"];

// ═════════════════════════════════════════════════════════════════════════
//  ROOT
// ═════════════════════════════════════════════════════════════════════════
export default function Recrutement() {
  const [tab, setTab] = useState("overview");
  const [filters, setFilters] = useState({ region: "", secteur: "", corner_key: "", poste: "", priorite: "", statut: "", age_min: "" });
  const [filterOpts, setFilterOpts] = useState({ regions: [], secteurs: [], corners: [] });
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newModal, setNewModal] = useState(false);
  const [ficheId, setFicheId] = useState(null);

  const loadDash = async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
      const r = await api.get("/rh/recrutement/dashboard", { params });
      setDashboard(r.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Erreur"); }
    setLoading(false);
  };
  useEffect(() => {
    api.get("/maintenance/filter-options").then(r => {
      setFilterOpts({ regions: r.data?.regions || [], secteurs: r.data?.secteurs || [], corners: r.data?.corners || [] });
    }).catch(() => {});
    loadDash(); // eslint-disable-next-line
  }, []);
  useEffect(() => { loadDash(); /* eslint-disable-next-line */ }, [filters.region, filters.secteur, filters.corner_key, filters.priorite, filters.statut, filters.age_min, filters.poste]);

  return (
    <div className="space-y-4" data-testid="rh-recrutement-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Recrutement</h1>
          <p className="text-slate-500 mt-1 text-sm">Suivez l'ensemble des besoins, candidats et recrutements de vos corners.</p>
        </div>
        <button onClick={() => setNewModal(true)} data-testid="rh-new-besoin"
                className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-bold bg-blue-600 hover:bg-blue-700 text-white shadow-sm">
          <Plus className="w-4 h-4"/> Demande de recrutement
        </button>
      </div>

      {/* Filtres */}
      <div className="bg-white rounded-2xl border border-slate-200 p-2.5 flex items-center gap-2 flex-wrap">
        <Filter className="w-4 h-4 text-slate-400 ml-1"/>
        <FilterSelect value={filters.region} onChange={v => setFilters({...filters, region: v})} options={filterOpts.regions} placeholder="Région" testid="rc-fr-region"/>
        <FilterSelect value={filters.secteur} onChange={v => setFilters({...filters, secteur: v})} options={filterOpts.secteurs} placeholder="Secteur" testid="rc-fr-secteur"/>
        <FilterCornerSelect value={filters.corner_key} onChange={v => setFilters({...filters, corner_key: v})} corners={filterOpts.corners} testid="rc-fr-corner"/>
        <input value={filters.poste} onChange={e => setFilters({...filters, poste: e.target.value})} data-testid="rc-fr-poste"
               placeholder="Poste" className="px-3 py-2 rounded-xl border border-slate-200 bg-white text-xs font-semibold text-slate-700 outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 w-32"/>
        <FilterSelect value={filters.priorite} onChange={v => setFilters({...filters, priorite: v})}
                      options={[{v:"critique",l:"Critique"},{v:"importante",l:"Importante"},{v:"standard",l:"Standard"}]} enrich placeholder="Priorité" testid="rc-fr-prio"/>
        <FilterSelect value={filters.statut} onChange={v => setFilters({...filters, statut: v})}
                      options={Object.entries(REC_STAT).map(([v,l])=>({v,l}))} enrich placeholder="Statut" testid="rc-fr-statut"/>
        <input type="number" value={filters.age_min} onChange={e => setFilters({...filters, age_min: e.target.value})} data-testid="rc-fr-age"
               placeholder="Anc. min (j)" className="px-3 py-2 rounded-xl border border-slate-200 bg-white text-xs font-semibold text-slate-700 outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 w-28"/>
        {Object.values(filters).some(Boolean) && (
          <button onClick={() => setFilters({ region: "", secteur: "", corner_key: "", poste: "", priorite: "", statut: "", age_min: "" })}
                  data-testid="rc-fr-reset"
                  className="ml-auto text-xs font-bold text-slate-500 hover:text-slate-800 flex items-center gap-1 px-2">
            <X className="w-3 h-3"/> Reset
          </button>
        )}
      </div>

      {/* KPI cards */}
      {dashboard && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          <KpiTile icon={Briefcase} color="blue" title="Besoins ouverts" value={dashboard.kpis.besoins_ouverts.value} sub={`dont ${dashboard.kpis.besoins_ouverts.critiques} critiques`} testid="rc-kpi-besoins"/>
          <KpiTile icon={Users} color="purple" title="Candidats en cours" value={dashboard.kpis.candidats_en_cours.value} sub={`${dashboard.kpis.candidats_en_cours.nouveaux} nouveaux`} testid="rc-kpi-candidats"/>
          <KpiTile icon={Calendar} color="amber" title="Entretiens à venir" value={dashboard.kpis.entretiens_a_venir.value} sub={`${dashboard.kpis.entretiens_a_venir.cette_semaine} cette semaine`} testid="rc-kpi-entretiens"/>
          <KpiTile icon={UserPlus} color="emerald" title="Recrutements en cours" value={dashboard.kpis.recrutements_encours.value} sub={`${dashboard.kpis.recrutements_encours.integ_retard} en retard`} testid="rc-kpi-encours"/>
          <KpiTile icon={Timer} color="orange" title="Délai moyen" value={dashboard.kpis.delai_moyen.value} unit={dashboard.kpis.delai_moyen.unit} sub="jusqu'au recrutement" testid="rc-kpi-delai"/>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1.5 border-b border-slate-200 pb-1 overflow-x-auto">
        {TABS.map(t => (
          <button key={t.k} onClick={() => setTab(t.k)} data-testid={`rc-tab-${t.k}`}
                  className={`px-4 py-2 rounded-t-xl text-sm font-semibold whitespace-nowrap transition ${
                    tab === t.k ? "bg-white border border-slate-200 border-b-white -mb-px text-slate-900" : "text-slate-500 hover:text-slate-800"
                  }`}>{t.l}</button>
        ))}
      </div>

      {/* Tab content */}
      {loading && !dashboard ? (
        <div className="py-24 grid place-items-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin"/></div>
      ) : (
        <>
          {tab === "overview" && dashboard && <><RecrutementPipeline onOpenFiche={setFicheId}/><OverviewTab dash={dashboard} onOpenFiche={setFicheId}/></>}
          {tab === "analyse" && <RecrutementAnalyse/>}
          {tab === "zones" && <ZonesTab onOpenFiche={setFicheId}/>}
          {tab === "besoins" && dashboard && <BesoinsTab rows={dashboard.besoins} onOpenFiche={setFicheId}/>}
          {tab === "candidats" && <CandidatsTab onOpenFiche={setFicheId}/>}
          {tab === "entretiens" && <EntretiensTab/>}
          {tab === "integrations" && dashboard && <IntegrationsTab data={dashboard.integrations}/>}
          {tab === "stats" && <StatsTab/>}
        </>
      )}

      {ficheId && <FicheDrawer id={ficheId} onClose={() => { setFicheId(null); loadDash(); }}/>}
      {newModal && <RecrutementRequestForm source="rh" onClose={() => setNewModal(false)} onSaved={() => { setNewModal(false); loadDash(); }}/>}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
//  TAB · Vue d'ensemble
// ═════════════════════════════════════════════════════════════════════════
function OverviewTab({ dash, onOpenFiche }) {
  const steps = [
    { k: "besoins", l: "Besoins ouverts", v: dash.pipeline.besoins, color: "#3b82f6" },
    { k: "candidats", l: "Candidats", v: dash.pipeline.candidats, color: "#8b5cf6" },
    { k: "entretiens", l: "Entretiens", v: dash.pipeline.entretiens, color: "#a855f7" },
    { k: "retenus", l: "Retenus", v: dash.pipeline.retenus, color: "#f59e0b" },
    { k: "integration", l: "Intégration", v: dash.pipeline.integration, color: "#10b981" },
  ];
  return (
    <div className="space-y-3" data-testid="rc-overview">
      {/* Pipeline visuel */}
      <div className="bg-white rounded-2xl border border-slate-200 p-4">
        <h3 className="font-display text-base font-bold text-slate-900 mb-3">Pipeline global</h3>
        <div className="flex items-stretch gap-2 overflow-x-auto">
          {steps.map((s, i) => (
            <div key={s.k} className="flex items-center gap-2 min-w-0 flex-1">
              <button data-testid={`rc-pipe-${s.k}`}
                      className="flex-1 rounded-2xl px-3 py-3 text-white shadow-sm hover:shadow-md hover:scale-[1.02] transition text-center"
                      style={{ background: s.color }}>
                <div className="text-[10px] font-bold uppercase tracking-wider opacity-80">{s.l}</div>
                <div className="font-display text-3xl font-bold leading-tight">{s.v}</div>
              </button>
              {i < steps.length - 1 && <ArrowRight className="w-4 h-4 text-slate-300 shrink-0"/>}
            </div>
          ))}
        </div>
      </div>

      {/* Row 1 : Besoins ouverts (2/3) + À surveiller (1/3) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
            <h3 className="font-display text-base font-bold text-slate-900">Besoins ouverts</h3>
            <span className="text-xs font-semibold text-slate-500">{dash.besoins.length} lignes</span>
          </div>
          <div className="overflow-x-auto max-h-[420px]">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-[10px] uppercase tracking-wider sticky top-0">
                <tr>
                  <th className="text-left px-3 py-2">Priorité</th>
                  <th className="text-left px-3 py-2">Poste</th>
                  <th className="text-left px-3 py-2">Corner</th>
                  <th className="text-left px-3 py-2">Secteur</th>
                  <th className="text-right px-3 py-2">Besoin depuis</th>
                  <th className="text-right px-3 py-2">Candidats</th>
                  <th className="text-left px-3 py-2">Statut</th>
                </tr>
              </thead>
              <tbody>
                {dash.besoins.slice(0, 30).map(r => {
                  const p = PRIO[r.priorite] || PRIO.standard;
                  return (
                    <tr key={r.id} onClick={() => onOpenFiche(r.id)} data-testid={`rc-besoin-${r.id}`}
                        className="border-t border-slate-100 hover:bg-blue-50/40 cursor-pointer">
                      <td className="px-3 py-2 whitespace-nowrap"><span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border ${p.cls}`}>{p.l}</span></td>
                      <td className="px-3 py-2 font-semibold text-slate-800 whitespace-nowrap">{r.poste}</td>
                      <td className="px-3 py-2 text-slate-600 whitespace-nowrap max-w-[200px] truncate">{r.corner || "—"}</td>
                      <td className="px-3 py-2 text-slate-500 text-xs whitespace-nowrap">{r.secteur || "—"}</td>
                      <td className={`px-3 py-2 text-right whitespace-nowrap ${r.age_jours >= 10 ? "text-rose-600 font-bold" : "text-slate-600"}`}>{r.age_jours} j</td>
                      <td className="px-3 py-2 text-right font-semibold text-slate-700">{r.candidats_count || 0}</td>
                      <td className="px-3 py-2"><span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${REC_STAT_CLS[r.statut] || "bg-slate-100 text-slate-500"}`}>{REC_STAT[r.statut] || r.statut}</span></td>
                    </tr>
                  );
                })}
                {dash.besoins.length === 0 && (
                  <tr><td colSpan={7} className="py-12 text-center text-slate-400 text-sm italic">Aucun besoin ouvert</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* À surveiller */}
        <div className="bg-white rounded-2xl border border-slate-200 p-4" data-testid="rc-surveille">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-4 h-4 text-amber-500"/>
            <h3 className="font-display text-base font-bold text-slate-900">À surveiller</h3>
          </div>
          <p className="text-[11px] text-slate-500 mb-3">Règles automatiques (non IA)</p>
          <div className="space-y-2">
            {dash.a_surveiller.map((s, i) => (
              <div key={i} className={`rounded-lg border p-2.5 ${s.severity === "critique" ? "bg-rose-50 border-rose-200" : "bg-amber-50 border-amber-200"}`} data-testid={`rc-surv-${i}`}>
                <div className="text-xs font-bold text-slate-800 leading-tight">{s.label}</div>
                <div className="text-[11px] text-slate-600 mt-0.5">{s.sub}</div>
              </div>
            ))}
            {dash.a_surveiller.length === 0 && <div className="py-6 text-center text-slate-400 text-sm italic">Aucun signal 🎉</div>}
          </div>
        </div>
      </div>

      {/* Row 2 : Recrutements en cours (2/3) + Sources donut (1/3) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div className="lg:col-span-2">
          <IntegrationsWidget data={dash.integrations}/>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 p-4" data-testid="rc-sources">
          <h3 className="font-display text-base font-bold text-slate-900 mb-2">Sources des candidats</h3>
          <SourcesDonut segments={dash.sources}/>
        </div>
      </div>
    </div>
  );
}

function IntegrationsWidget({ data }) {
  const [t, setT] = useState("preparation");
  const rows = data[t === "preparation" ? "en_preparation" : t === "recent" ? "recemment_integre" : "en_retard"] || [];
  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid="rc-integ-widget">
      <div className="px-4 py-2.5 border-b border-slate-100 flex items-center justify-between flex-wrap gap-2">
        <h3 className="font-display text-base font-bold text-slate-900">Recrutements en cours</h3>
        <div className="flex gap-1">
          <TabPill active={t === "preparation"} onClick={() => setT("preparation")} testid="rc-integ-tab-prep">En préparation ({data.en_preparation?.length || 0})</TabPill>
          <TabPill active={t === "recent"} onClick={() => setT("recent")} testid="rc-integ-tab-recent">Récemment intégré ({data.recemment_integre?.length || 0})</TabPill>
          <TabPill active={t === "retard"} onClick={() => setT("retard")} testid="rc-integ-tab-retard">En retard ({data.en_retard?.length || 0})</TabPill>
        </div>
      </div>
      <div className="overflow-x-auto max-h-[340px]">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-[10px] uppercase tracking-wider sticky top-0">
            <tr>
              <th className="text-left px-3 py-2">Collaborateur</th>
              <th className="text-left px-3 py-2">Poste</th>
              <th className="text-left px-3 py-2">Corner</th>
              <th className="text-right px-3 py-2">Arrivée</th>
              <th className="text-left px-3 py-2">Progression</th>
              <th className="text-left px-3 py-2">Statut</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(i => (
              <tr key={i.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`rc-integ-row-${i.id}`}>
                <td className="px-3 py-2 font-semibold text-slate-800 whitespace-nowrap">{i.collab_nom}</td>
                <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{i.poste || "—"}</td>
                <td className="px-3 py-2 text-slate-600 whitespace-nowrap max-w-[180px] truncate">{i.corner || "—"}</td>
                <td className={`px-3 py-2 text-right whitespace-nowrap ${t === "retard" ? "text-rose-600 font-bold" : "text-slate-600"}`}>{i.date_arrivee}</td>
                <td className="px-3 py-2 min-w-[120px]">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full ${i.progress >= 80 ? "bg-emerald-500" : i.progress >= 40 ? "bg-blue-500" : "bg-amber-500"}`} style={{ width: `${i.progress}%` }}/>
                    </div>
                    <span className="text-[11px] font-semibold text-slate-500 w-9 text-right">{i.progress}%</span>
                  </div>
                </td>
                <td className="px-3 py-2"><span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 whitespace-nowrap">{i.statut}</span></td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={6} className="py-10 text-center text-slate-400 text-sm italic">Aucune ligne</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SourcesDonut({ segments }) {
  const total = segments.reduce((s, x) => s + x.count, 0);
  const data = segments.length ? segments : [{ l: "—", count: 1 }];
  return (
    <div className="flex items-center gap-3">
      <div className="relative w-28 h-28 shrink-0">
        <ResponsiveContainer>
          <PieChart>
            <Pie data={data} dataKey="count" innerRadius={36} outerRadius={54} paddingAngle={2} stroke="none">
              {data.map((_, i) => <Cell key={i} fill={DONUT_COLORS[i % DONUT_COLORS.length]}/>)}
            </Pie>
            <Tooltip contentStyle={{ borderRadius: 6, fontSize: 11 }}/>
          </PieChart>
        </ResponsiveContainer>
        <div className="absolute inset-0 grid place-items-center pointer-events-none">
          <div className="text-center">
            <div className="font-display text-xl font-bold text-slate-900 leading-none">{total}</div>
            <div className="text-[9px] font-bold uppercase text-slate-400 mt-0.5">Total</div>
          </div>
        </div>
      </div>
      <div className="flex-1 min-w-0 space-y-1">
        {segments.slice(0, 8).map((s, i) => (
          <div key={s.k} className="flex items-center gap-1.5 text-xs">
            <span className="w-2 h-2 rounded-full shrink-0" style={{ background: DONUT_COLORS[i % DONUT_COLORS.length] }}/>
            <span className="text-slate-600 flex-1 truncate">{s.l}</span>
            <span className="font-semibold text-slate-800">{s.count}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
//  TAB · Besoins & Postes
// ═════════════════════════════════════════════════════════════════════════
function BesoinsTab({ rows, onOpenFiche }) {
  const [q, setQ] = useState("");
  const [sortBy, setSortBy] = useState({ k: "age", dir: -1 });
  const filtered = rows.filter(r => !q || `${r.poste} ${r.corner} ${r.secteur}`.toLowerCase().includes(q.toLowerCase()));
  const sorted = [...filtered].sort((a, b) => {
    if (sortBy.k === "age") return sortBy.dir * ((b.age_jours || 0) - (a.age_jours || 0));
    if (sortBy.k === "candidats") return sortBy.dir * ((b.candidats_count || 0) - (a.candidats_count || 0));
    return sortBy.dir * String(a[sortBy.k] || "").localeCompare(String(b[sortBy.k] || ""));
  });

  const exportCsv = () => {
    const cols = ["priorite","poste","corner","secteur","statut","age_jours","candidats_count"];
    const lines = [cols.join(";"), ...sorted.map(r => cols.map(k => `"${(r[k] ?? "").toString().replace(/"/g,'""')}"`).join(";"))];
    const blob = new Blob(["\uFEFF" + lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "besoins_recrutement.csv"; a.click();
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid="rc-tab-besoins">
      <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-slate-100">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Rechercher poste, corner, secteur…" data-testid="rc-besoins-search"
                 className="w-full pl-9 pr-3 py-2 rounded-xl border border-slate-200 bg-slate-50 text-sm outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400"/>
        </div>
        <button onClick={exportCsv} data-testid="rc-besoins-export" className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-slate-200 text-xs font-semibold text-slate-600 hover:bg-slate-50"><Download className="w-3.5 h-3.5"/> Export CSV</button>
      </div>
      <div className="overflow-x-auto max-h-[70vh]">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-[10px] uppercase tracking-wider sticky top-0">
            <tr>
              <ThSort k="priorite" sort={sortBy} setSort={setSortBy}>Priorité</ThSort>
              <ThSort k="poste" sort={sortBy} setSort={setSortBy}>Poste</ThSort>
              <ThSort k="corner" sort={sortBy} setSort={setSortBy}>Corner</ThSort>
              <ThSort k="secteur" sort={sortBy} setSort={setSortBy}>Secteur</ThSort>
              <ThSort k="age" sort={sortBy} setSort={setSortBy} right>Besoin depuis</ThSort>
              <ThSort k="candidats" sort={sortBy} setSort={setSortBy} right>Candidats</ThSort>
              <ThSort k="statut" sort={sortBy} setSort={setSortBy}>Statut</ThSort>
              <th className="text-right px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map(r => {
              const p = PRIO[r.priorite] || PRIO.standard;
              return (
                <tr key={r.id} className="border-t border-slate-100 hover:bg-blue-50/40" data-testid={`rc-brow-${r.id}`}>
                  <td className="px-3 py-2 whitespace-nowrap"><span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border ${p.cls}`}>{p.l}</span></td>
                  <td className="px-3 py-2 font-semibold text-slate-800 whitespace-nowrap">{r.poste}</td>
                  <td className="px-3 py-2 text-slate-600 whitespace-nowrap max-w-[200px] truncate">{r.corner || "—"}</td>
                  <td className="px-3 py-2 text-slate-500 text-xs whitespace-nowrap">{r.secteur || "—"}</td>
                  <td className={`px-3 py-2 text-right whitespace-nowrap ${r.age_jours >= 10 ? "text-rose-600 font-bold" : "text-slate-600"}`}>{r.age_jours} j</td>
                  <td className="px-3 py-2 text-right font-semibold text-slate-700">{r.candidats_count || 0}</td>
                  <td className="px-3 py-2"><span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${REC_STAT_CLS[r.statut] || "bg-slate-100 text-slate-500"}`}>{REC_STAT[r.statut] || r.statut}</span></td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button onClick={() => onOpenFiche(r.id)} data-testid={`rc-brow-view-${r.id}`} className="w-7 h-7 grid place-items-center rounded-lg hover:bg-white"><Eye className="w-4 h-4 text-slate-500"/></button>
                  </td>
                </tr>
              );
            })}
            {sorted.length === 0 && <tr><td colSpan={8} className="py-12 text-center text-slate-400 text-sm italic">Aucun besoin</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ThSort({ k, sort, setSort, right, children }) {
  const active = sort.k === k;
  return (
    <th className={`px-3 py-2 select-none cursor-pointer ${right ? "text-right" : "text-left"}`}
        onClick={() => setSort({ k, dir: active ? -sort.dir : -1 })}>
      <span className={`inline-flex items-center gap-1 ${active ? "text-slate-700" : ""}`}>
        {children}
        {active && (sort.dir === -1 ? <ChevronDown className="w-3 h-3"/> : <ChevronDown className="w-3 h-3 rotate-180"/>)}
      </span>
    </th>
  );
}

// ═════════════════════════════════════════════════════════════════════════
//  TAB · Candidats (pipeline kanban + fiche)
// ═════════════════════════════════════════════════════════════════════════
function CandidatsTab({ onOpenFiche }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [view, setView] = useState("pipeline"); // pipeline | table
  const [detail, setDetail] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/rh/candidats"); setRows(r.data || []); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Erreur"); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const filtered = rows.filter(c => !q || `${c.prenom} ${c.nom} ${c.email||""} ${c.poste_vise||""} ${c.corner||""}`.toLowerCase().includes(q.toLowerCase()));
  const cols = ["nouveau","a_contacter","contacte","entretien_prevu","entretien_realise","retenu","refuse","vivier"];
  const byStat = useMemo(() => Object.fromEntries(cols.map(k => [k, filtered.filter(c => c.statut === k)])), [filtered]);

  const [embauche, setEmbauche] = useState(null);
  const move = async (c, newStat) => {
    try { await api.patch(`/rh/candidats/${c.id}`, { statut: newStat }); toast.success(newStat === "retenu" ? "Candidat retenu — vous pouvez créer l'intégration" : "Statut mis à jour"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Erreur"); }
  };

  return (
    <div className="space-y-3" data-testid="rc-tab-candidats">
      {embauche && <EmbaucheModal candidat={embauche} onClose={() => setEmbauche(null)} onDone={load}/>}
      <div className="bg-white rounded-2xl border border-slate-200 p-2.5 flex items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Rechercher candidat…" data-testid="rc-cand-search"
                 className="w-full pl-9 pr-3 py-2 rounded-xl border border-slate-200 bg-slate-50 text-sm outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400"/>
        </div>
        <div className="flex gap-1">
          <TabPill active={view === "pipeline"} onClick={() => setView("pipeline")} testid="rc-cand-view-pipe">Pipeline</TabPill>
          <TabPill active={view === "table"} onClick={() => setView("table")} testid="rc-cand-view-table">Tableau</TabPill>
        </div>
      </div>

      {loading ? (
        <div className="py-24 grid place-items-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin"/></div>
      ) : view === "pipeline" ? (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2 overflow-x-auto">
          {cols.map(k => (
            <div key={k} className="bg-slate-50 rounded-xl p-2 min-h-[300px]" data-testid={`rc-cand-col-${k}`}>
              <div className="flex items-center justify-between px-1 pb-2 border-b border-slate-200">
                <span className={`text-[9px] font-bold uppercase px-2 py-0.5 rounded-full ${CAND_STAT_CLS[k]}`}>{CAND_STAT[k]}</span>
                <span className="text-xs font-bold text-slate-500">{byStat[k].length}</span>
              </div>
              <div className="space-y-2 pt-2">
                {byStat[k].slice(0, 30).map(c => (
                  <div key={c.id} onClick={() => setDetail(c)} data-testid={`rc-cand-card-${c.id}`}
                       className="bg-white rounded-lg border border-slate-200 p-2 hover:border-blue-300 cursor-pointer">
                    <div className="text-xs font-semibold text-slate-800 truncate">{c.prenom} {c.nom}</div>
                    <div className="text-[10px] text-slate-500 truncate">{c.poste_vise || "—"}</div>
                    <div className="text-[9px] text-slate-400 truncate mt-0.5">{c.source || "—"} · {c.corner || "—"}</div>
                    {c.note && <div className="flex items-center gap-0.5 mt-1"><Star className="w-2.5 h-2.5 text-amber-500 fill-amber-500"/><span className="text-[10px] font-semibold text-amber-600">{c.note}</span></div>}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto max-h-[70vh]">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-[10px] uppercase tracking-wider sticky top-0">
                <tr>
                  <th className="text-left px-3 py-2">Nom</th>
                  <th className="text-left px-3 py-2">Poste</th>
                  <th className="text-left px-3 py-2">Corner</th>
                  <th className="text-left px-3 py-2">Source</th>
                  <th className="text-left px-3 py-2">Contact</th>
                  <th className="text-left px-3 py-2">Statut</th>
                  <th className="text-right px-3 py-2">Note</th>
                </tr>
              </thead>
              <tbody>
                {filtered.slice(0, 300).map(c => (
                  <tr key={c.id} onClick={() => setDetail(c)} className="border-t border-slate-100 hover:bg-blue-50/40 cursor-pointer" data-testid={`rc-cand-row-${c.id}`}>
                    <td className="px-3 py-2 font-semibold text-slate-800 whitespace-nowrap">{c.prenom} {c.nom}</td>
                    <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{c.poste_vise || "—"}</td>
                    <td className="px-3 py-2 text-slate-600 whitespace-nowrap max-w-[180px] truncate">{c.corner || "—"}</td>
                    <td className="px-3 py-2 text-slate-500 text-xs whitespace-nowrap">{c.source || "—"}</td>
                    <td className="px-3 py-2 text-xs text-slate-500 whitespace-nowrap">{c.email || c.telephone || "—"}</td>
                    <td className="px-3 py-2"><span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${CAND_STAT_CLS[c.statut] || "bg-slate-100"}`}>{CAND_STAT[c.statut] || c.statut}</span></td>
                    <td className="px-3 py-2 text-right text-slate-500">{c.note || "—"}</td>
                  </tr>
                ))}
                {filtered.length === 0 && <tr><td colSpan={7} className="py-12 text-center text-slate-400 text-sm italic">Aucun candidat</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {detail && <CandidatDrawer candidat={detail} onClose={() => setDetail(null)} onMove={move} onSaved={load} onEmbauche={c => { setDetail(null); setEmbauche(c); }}/>}
    </div>
  );
}

function CandidatDrawer({ candidat, onClose, onMove, onSaved, onEmbauche }) {
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="fixed inset-y-0 right-0 w-full sm:w-[480px] bg-white shadow-2xl border-l border-slate-200 flex flex-col" data-testid="rc-cand-drawer">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h2 className="font-display text-xl font-bold text-slate-900">{candidat.prenom} {candidat.nom}</h2>
            <div className="text-xs text-slate-500 mt-0.5">{candidat.poste_vise || "—"} · {candidat.corner || "—"}</div>
          </div>
          <button onClick={onClose} className="w-8 h-8 grid place-items-center rounded-lg hover:bg-slate-100"><X className="w-4 h-4"/></button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          <div className="grid grid-cols-2 gap-2 text-xs">
            <InfoRow icon={Mail} label="Email" value={candidat.email || "—"}/>
            <InfoRow icon={Phone} label="Téléphone" value={candidat.telephone || "—"}/>
            <InfoRow icon={Users} label="Source" value={candidat.source || "—"}/>
            <InfoRow icon={Star} label="Note" value={candidat.note || "—"}/>
          </div>

          {/* Pipeline candidat */}
          <div>
            <div className="text-[11px] font-bold uppercase text-slate-500 mb-2">Pipeline candidat</div>
            <div className="grid grid-cols-2 gap-1">
              {["nouveau","a_contacter","contacte","entretien_prevu","entretien_realise","retenu","refuse","vivier"].map(k => (
                <button key={k} onClick={() => onMove(candidat, k)} data-testid={`rc-cand-move-${k}`}
                        className={`text-[11px] font-semibold px-2 py-1.5 rounded-lg border transition ${
                          candidat.statut === k ? `${CAND_STAT_CLS[k]} border-current` : "bg-white text-slate-600 border-slate-200 hover:border-blue-300"
                        }`}>{CAND_STAT[k]}</button>
              ))}
            </div>
            {candidat.statut === "retenu" && <button onClick={() => onEmbauche(candidat)} data-testid="rc-cand-embaucher" className="mt-2 w-full h-9 rounded-lg bg-emerald-600 text-white text-xs font-bold">Embaucher → créer l'intégration</button>}
            {candidat.statut === "embauche" && <div className="mt-2 text-[11px] font-semibold text-emerald-700">Embauché(e) · intégration créée</div>}
          </div>

          {/* Historique */}
          <div>
            <div className="text-[11px] font-bold uppercase text-slate-500 mb-2">Historique</div>
            <div className="space-y-1.5">
              {(candidat.history || []).slice(-8).reverse().map((h, i) => (
                <div key={i} className="text-xs bg-slate-50 rounded-lg p-2">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-slate-700">{h.action}</span>
                    <span className="text-[10px] text-slate-400">{new Date(h.at).toLocaleString("fr-FR")}</span>
                  </div>
                  {(h.from || h.to) && <div className="text-[11px] text-slate-500 mt-0.5">{h.from || "∅"} → <b>{h.to || "∅"}</b></div>}
                  {h.by_name && <div className="text-[10px] text-slate-400 mt-0.5">par {h.by_name}</div>}
                </div>
              ))}
              {(!candidat.history || candidat.history.length === 0) && <div className="text-[11px] text-slate-400 italic">Aucun historique</div>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="bg-slate-50 rounded-lg p-2">
      <div className="text-[10px] font-bold uppercase text-slate-400 flex items-center gap-1"><Icon className="w-3 h-3"/>{label}</div>
      <div className="text-slate-800 font-semibold mt-0.5 truncate">{value}</div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
//  TAB · Entretiens
// ═════════════════════════════════════════════════════════════════════════
function EntretiensTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { (async () => {
    setLoading(true);
    try { const r = await api.get("/rh/entretiens"); setRows(r.data || []); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Erreur"); }
    setLoading(false);
  })(); }, []);
  const today = new Date().toISOString().slice(0, 10);
  const a_venir = rows.filter(e => (e.date || "") >= today && e.statut === "planifie");
  const realises = rows.filter(e => e.statut === "realise");
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="rc-tab-entretiens">
      <EntretiensList title={`À venir (${a_venir.length})`} rows={a_venir} highlight="planifie"/>
      <EntretiensList title={`Réalisés (${realises.length})`} rows={realises} highlight="realise"/>
      {loading && <div className="col-span-full py-24 grid place-items-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin"/></div>}
    </div>
  );
}
function EntretiensList({ title, rows, highlight }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-100">
        <h3 className="font-display text-base font-bold text-slate-900">{title}</h3>
      </div>
      <div className="max-h-[520px] overflow-y-auto divide-y divide-slate-100">
        {rows.slice(0, 60).map(e => (
          <div key={e.id} className="px-4 py-2.5 flex items-center gap-3" data-testid={`rc-ent-${e.id}`}>
            <div className="w-10 h-10 rounded-lg bg-blue-50 grid place-items-center text-blue-700 shrink-0">
              <Calendar className="w-4 h-4"/>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-slate-800">{e.date} · {e.duree_min || 45} min</div>
              <div className="text-[11px] text-slate-500">{e.type} · par {e.interviewer_nom || "—"}</div>
            </div>
            <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${
              e.statut === "planifie" ? "bg-blue-100 text-blue-700" :
              e.statut === "realise" ? "bg-emerald-100 text-emerald-700" :
              "bg-slate-100 text-slate-500"
            }`}>{e.statut}</span>
            {e.statut === "realise" && !e.compte_rendu && <AlertTriangle className="w-4 h-4 text-amber-500" title="CR manquant"/>}
          </div>
        ))}
        {rows.length === 0 && <div className="py-16 text-center text-slate-400 text-sm italic">Aucun entretien</div>}
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
//  TAB · Intégrations
// ═════════════════════════════════════════════════════════════════════════
function IntegrationsTab({ data }) {
  return (
    <div className="space-y-3" data-testid="rc-tab-integrations">
      <IntegrationsWidget data={data}/>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
//  TAB · Statistiques
// ═════════════════════════════════════════════════════════════════════════
function StatsTab() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/rh/recrutement/stats").then(r => setStats(r.data)).catch(e => toast.error(formatApiError(e.response?.data?.detail) || "Erreur")); }, []);
  if (!stats) return <div className="py-24 grid place-items-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin"/></div>;
  const deltaDelai = stats.besoins.delai_moyen - stats.besoins.delai_moyen_prev;
  const deltaConv = stats.candidats.taux_conversion_30j - stats.candidats.taux_conversion_60j_prev;
  return (
    <div className="space-y-3" data-testid="rc-tab-stats">
      {/* Row KPI compare */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatBox label="Besoins ouverts" value={stats.besoins.ouverts}/>
        <StatBox label="Pourvus (30j)" value={stats.besoins.pourvus_30j} sub={`vs ${stats.besoins.pourvus_60j_prev} période précédente`}/>
        <StatBox label="Délai moyen" value={`${stats.besoins.delai_moyen} j`} sub={`${deltaDelai >= 0 ? "+" : ""}${deltaDelai.toFixed(1)} j vs période -1`} bad={deltaDelai > 0}/>
        <StatBox label="Taux conversion (30j)" value={`${stats.candidats.taux_conversion_30j}%`} sub={`${deltaConv >= 0 ? "+" : ""}${deltaConv.toFixed(1)}pts vs période -1`} bad={deltaConv < 0}/>
      </div>

      {/* Répartitions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <BarBox title="Par secteur" rows={stats.repartition.par_secteur} color="#3b82f6"/>
        <BarBox title="Top corners" rows={stats.repartition.par_corner} color="#8b5cf6"/>
        <BarBox title="Par poste" rows={stats.repartition.par_poste} color="#10b981"/>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div className="lg:col-span-1 bg-white rounded-2xl border border-slate-200 p-4">
          <h3 className="font-display text-base font-bold text-slate-900 mb-2">Candidats</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-slate-600">Total</span><b>{stats.candidats.total}</b></div>
            <div className="flex justify-between"><span className="text-slate-600">En cours</span><b>{stats.candidats.en_cours}</b></div>
            <div className="flex justify-between"><span className="text-slate-600">Entretiens réalisés</span><b>{stats.candidats.entretiens_realises}</b></div>
          </div>
        </div>
        <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 p-4">
          <h3 className="font-display text-base font-bold text-slate-900 mb-2">Sources des candidats</h3>
          <SourcesDonut segments={stats.candidats.sources}/>
        </div>
      </div>
    </div>
  );
}
function StatBox({ label, value, sub, bad }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4">
      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{label}</div>
      <div className="font-display text-3xl font-bold text-slate-900 mt-0.5">{value}</div>
      {sub && <div className={`text-[11px] font-semibold mt-0.5 ${bad ? "text-rose-600" : "text-emerald-600"}`}>{sub}</div>}
    </div>
  );
}
function BarBox({ title, rows, color }) {
  const data = (rows || []).slice(0, 8);
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4">
      <h3 className="font-display text-base font-bold text-slate-900 mb-2">{title}</h3>
      <div style={{ height: Math.max(160, data.length * 26) }}>
        <ResponsiveContainer>
          <BarChart data={data} layout="vertical" margin={{ left: 4, right: 8, top: 4, bottom: 4 }}>
            <XAxis type="number" hide/>
            <YAxis dataKey="k" type="category" width={110} tick={{ fontSize: 11 }} axisLine={false} tickLine={false}/>
            <Tooltip contentStyle={{ borderRadius: 6, fontSize: 11 }}/>
            <Bar dataKey="count" fill={color} radius={[0, 6, 6, 0]}/>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
//  Fiche recrutement — drawer
// ═════════════════════════════════════════════════════════════════════════
function FicheDrawer({ id, onClose }) {
  const [rec, setRec] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const load = async () => {
    setLoading(true);
    try { const r = await api.get(`/rh/recrutements/${id}`); setRec(r.data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Erreur"); onClose(); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const setStatut = async (s) => {
    setSaving(true);
    try { await api.patch(`/rh/recrutements/${id}`, { statut: s }); toast.success(`Statut → ${REC_STAT[s]}`); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Erreur"); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="fixed inset-y-0 right-0 w-full sm:w-[720px] bg-white shadow-2xl border-l border-slate-200 flex flex-col" data-testid="rc-fiche">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <h2 className="font-display text-xl font-bold text-slate-900">{rec?.poste || "Fiche recrutement"}</h2>
            <div className="text-xs text-slate-500 mt-0.5">{rec?.corner || "—"} · {rec?.contrat || "—"}</div>
          </div>
          <button onClick={onClose} className="w-8 h-8 grid place-items-center rounded-lg hover:bg-slate-100"><X className="w-4 h-4"/></button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {loading || !rec ? <div className="py-16 grid place-items-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin"/></div> : (
            <>
              {/* Infos */}
              <div className="grid grid-cols-2 gap-2 text-xs">
                <InfoRow icon={AlertTriangle} label="Priorité" value={PRIO[rec.priorite]?.l || rec.priorite}/>
                <InfoRow icon={Calendar} label="Date besoin" value={rec.date_besoin || "—"}/>
                <InfoRow icon={Users} label="Nb postes" value={rec.nb_postes || 1}/>
                <InfoRow icon={Calendar} label="Arrivée souhaitée" value={rec.date_arrivee || "—"}/>
                <InfoRow icon={Briefcase} label="Manager demandeur" value={rec.demandeur_nom || rec.responsable_nom || "—"}/>
                <InfoRow icon={UserPlus} label="Nb candidats" value={rec.candidats_count || (rec.candidats?.length || 0)}/>
              </div>

              {/* Workflow timeline */}
              <div>
                <div className="text-[11px] font-bold uppercase text-slate-500 mb-2">Workflow</div>
                <div className="grid grid-cols-3 md:grid-cols-9 gap-1">
                  {rec.workflow.map(s => (
                    <button key={s.k} onClick={() => setStatut(s.k)} disabled={saving} data-testid={`rc-wf-${s.k}`}
                            className={`text-[10px] font-semibold px-1.5 py-2 rounded-lg text-center transition ${
                              s.current ? "bg-blue-600 text-white" :
                              s.done ? "bg-emerald-100 text-emerald-700" :
                              "bg-slate-50 text-slate-400 hover:bg-slate-100"
                            }`}>
                      <div className="flex justify-center mb-1">
                        {s.done && !s.current ? <CheckCircle2 className="w-3.5 h-3.5"/> : <Circle className="w-3.5 h-3.5"/>}
                      </div>
                      {s.l}
                    </button>
                  ))}
                </div>
              </div>

              {/* Candidats liés */}
              <div>
                <div className="text-[11px] font-bold uppercase text-slate-500 mb-2 flex items-center gap-1.5"><Users className="w-3 h-3"/> Candidats associés ({rec.candidats?.length || 0})</div>
                <div className="space-y-1 max-h-[200px] overflow-y-auto">
                  {(rec.candidats || []).map(c => (
                    <div key={c.id} className="flex items-center gap-2 p-2 rounded-lg bg-slate-50">
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-semibold text-slate-800 truncate">{c.prenom} {c.nom}</div>
                        <div className="text-[10px] text-slate-500">{c.source || "—"}</div>
                      </div>
                      <span className={`text-[9px] font-bold uppercase px-2 py-0.5 rounded-full ${CAND_STAT_CLS[c.statut] || "bg-slate-100"}`}>{CAND_STAT[c.statut] || c.statut}</span>
                    </div>
                  ))}
                  {(!rec.candidats || rec.candidats.length === 0) && <div className="text-[11px] text-slate-400 italic">Aucun candidat</div>}
                </div>
              </div>

              {/* Historique */}
              <div>
                <div className="text-[11px] font-bold uppercase text-slate-500 mb-2 flex items-center gap-1.5"><Clock className="w-3 h-3"/> Historique</div>
                <div className="space-y-1.5">
                  {(rec.history || []).slice(-10).reverse().map((h, i) => (
                    <div key={i} className="text-xs bg-slate-50 rounded-lg p-2">
                      <div className="flex items-center justify-between">
                        <span className="font-semibold text-slate-700">{h.action}</span>
                        <span className="text-[10px] text-slate-400">{new Date(h.at).toLocaleString("fr-FR")}</span>
                      </div>
                      {(h.from || h.to) && <div className="text-[11px] text-slate-500 mt-0.5">{h.from || "∅"} → <b>{h.to || "∅"}</b></div>}
                    </div>
                  ))}
                  {(!rec.history || rec.history.length === 0) && <div className="text-[11px] text-slate-400 italic">Aucun historique</div>}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
//  Modal nouveau besoin
// ═════════════════════════════════════════════════════════════════════════

// ═════════════════════════════════════════════════════════════════════════
//  Shared micro-components
// ═════════════════════════════════════════════════════════════════════════
function KpiTile({ icon: Icon, color, title, value, unit, sub, testid }) {
  const bg = { blue: "bg-blue-50 text-blue-600", purple: "bg-purple-50 text-purple-600", amber: "bg-amber-50 text-amber-600", emerald: "bg-emerald-50 text-emerald-600", orange: "bg-orange-50 text-orange-600" }[color] || "bg-slate-50 text-slate-600";
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-4" data-testid={testid}>
      <div className={`w-10 h-10 rounded-xl ${bg} grid place-items-center`}><Icon className="w-5 h-5"/></div>
      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mt-2">{title}</div>
      <div className="flex items-baseline gap-1">
        <div className="font-display text-3xl font-bold text-slate-900">{value}</div>
        {unit && <div className="text-sm font-semibold text-slate-500">{unit}</div>}
      </div>
      <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>
    </div>
  );
}
function TabPill({ active, onClick, testid, children }) {
  return (
    <button onClick={onClick} data-testid={testid} className={`text-[11px] font-bold px-3 py-1.5 rounded-full transition ${active ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{children}</button>
  );
}
function FilterSelect({ value, onChange, options, placeholder, testid, enrich, single = false }) {
  const opts = (options || []).map(o => typeof o === "string" ? { value: o, label: o } : { value: o.v, label: enrich ? o.l : (o.l || o.v) });
  return <FilterMultiSelect label={placeholder} placeholder={placeholder} testid={testid} options={opts} single={single} size="lg" menuWidth={240}
                            value={(value || "").split(",").filter(Boolean)} onChange={arr => onChange(arr.join(","))}/>;
}
function FilterCornerSelect({ value, onChange, corners, testid }) {
  return <FilterMultiSelect label="Corner" placeholder="Corner" testid={testid} size="lg" menuWidth={260} className="max-w-[220px]"
                            options={(corners || []).map(c => ({ value: c.corner_key, label: c.corner || c.corner_key }))}
                            value={(value || "").split(",").filter(Boolean)} onChange={arr => onChange(arr.join(","))}/>;
}
